import { Pathnames } from 'next-intl/navigation';
import { routes } from '@core/types/routes.types';
import type { RouteTags } from '@core/types/routeTags.types';
import { generateLocalizedPathnames } from '@next/pathname/generatePathnames';
import { tags } from './template.tags';
import { getContentfulLocalesByCodes } from './contentful.locales';
import config from './publishinghub.config.json';

type Locales = (typeof locales)[number];
type RouterLocales = Locales['router'];
// export type RouteTags = {
//   [K in keyof typeof routes]: string[];
// };

type TemplateConfig = {
  localization: {
    locales: typeof locales;
    fileSystemLocale: Locales['router'];
    defaultRouterLocale: Locales['router'];
  };
  navigation: {
    tags: Partial<RouteTags>;
    routes: typeof routes;
    pathnames: Pathnames<RouterLocales[]>;
  };
  title: string;

  // TODO - This needs to be implemented
  // See - https://stadion.atlassian.net/browse/V3DS-1558
  crests?: {
    dark?: string;
    light?: string;
  };
  staticParams: {
    enable: boolean;
    limits: { [key: string]: number };
  };
  SPORTS_DATA_API: {
    ENDPOINTS: Partial<{
      LEAGUE_TABLES: string;
      HEAD_TO_HEAD: string;
      FORM_GUIDE: string;
      COMMENTARY: string;
      OPTA_COMMENTARY: string;
      LEAGUE_TABLES_GROUPS: string;
      TEAM_SUMMARY_STATS: string;
      LIVE_SCORES: string;
      LIVE_LEAGUE_TABLES: string;
    }>;
  };
  LEAGUE_TABLES_DATA: {
    RANKING_COLOURS: {
      FIRST_RANKING_DIVIDER_COLOUR: string;
      SECOND_RANKING_DIVIDER_COLOUR: string;
      BOTTOM_RANKING_DIVIDER_COLOUR: string;
    };
  };
};

/**
 * An array of [router: string, contentful: string]
 *
 * Router locale will be the one used in the URL.
 * Contentful locale must match the locales selected in Contentful
 *
 * Locales should be also updated in '/src/lib/dayjs.ts'
 * Locales should be also updated in '/src/middleware.ts' in config.matcher
 * Locales should be also updated in GraphQL's queries about metadata /src/graphql/queries/metadata
 */
const defaultLocales = ['en-GB', 'fr-FR', 'it-IT'];
const locales = config.locales
  ? getContentfulLocalesByCodes(config.locales)
  : getContentfulLocalesByCodes(defaultLocales);

const defaultTitle = 'Scottish FA';
const title = config.title || defaultTitle;

const templateConfig: TemplateConfig = {
  /**
   * Name is used for the title of the website used in metadata (generateLayoutMetadata.ts)
   * For example, you can use something like Chelsea Football Club, or Six Nations
   */
  // TODO: Update this value to a Env variable.
  title,
  navigation: {
    tags,
    routes,
    /**
     * Documentation : https://next-intl-docs.vercel.app/docs/routing/navigation#localized-pathnames
     * Please, read the comment about fileSystemLocale
     *
     * Example :
     * {
     *     [routes.ARTICLE]: {
     *         en: /news/routes.ARTICLE,
     *         fr: '/infos/[article]',
     *         it: '/notizia/[article]',
     *      },
     * }
     */
    pathnames: generateLocalizedPathnames(
      locales.map((locale) => locale.router)
    ),
  },
  localization: {
    /**
     * The default locale used on the website
     */
    defaultRouterLocale: 'en',
    /**
     * In navigation.pathnames, a config looks like this :
     * {
     *   [routes.NEWS_LISTING]: {
     *     en: routes.NEWS_LISTING,
     *     fr: '/infos',
     *     it: '/notizia',
     *   },
     *
     * }
     * The fileSystemLocale MUST match the one that use the exact file system naming. In this example, it's 'en'. Otherwise, revalidation will fail.
     */
    fileSystemLocale: 'en',
    locales,
  },
  staticParams: {
    /**
     * Enable or disable static params based on the .env variable. By default, it will be disabled
     */
    enable: !!process.env.NEXT_GENERATE_STATIC_PARAMS,
    limits: {
      /**
       * This will set a limit on how many static pages will be generated at build time.
       * When you have nested dynamic segment, you need to multiply them to know the exact output number.
       * For example :
       * └── [locale]/ --> Number of locales (ex: 3)
       *     └── news/
       *         └── [article]/ --> Article limit set below (ex: 20). = Locales * Limit = 3 * 20 = 60 generated articles
       *             └── [deeperContent] --> Deeper content limit set below (ex: 30). = Locales * Articles * Limit = 3 * 20 * 30 = 1 800 generated deeper content
       *
       * If you want to generate everything, just pass a very big number (2147483647 is the biggest int)
       */
      [routes.ARTICLE]: 300,
      [routes.CONTENT]: 300,
      [routes.NEWS_LISTING]: 50,
      [routes.NEWS_CATEGORY]: 50,
      [routes.NEWS_TAG]: 50,
      [routes.TEAM]: 50,
      [routes.PROFILE]: 150,
      [routes.TEAM_YOUTH]: 50,
      [routes.PROFILE_YOUTH]: 150,
      MATCHES_TEAM: 50,
      [routes.FIXTURES]: 300,
      [routes.FIXTURES_COMPETITION]: 50,
      [routes.RESULTS_COMPETITION]: 50,
      [routes.RESULTS_COMPETITION_SEASON]: 50,
      [routes.TABLE_COMPETITION]: 50,
      [routes.TABLE_COMPETITION_SEASON]: 50,
      [routes.MATCH_CENTRE]: 300,
    },
  },
  SPORTS_DATA_API: {
    ENDPOINTS: {
      LEAGUE_TABLES: 'league-table/latest',
      HEAD_TO_HEAD: 'historical/head-to-head',
      FORM_GUIDE: 'historical/form-guide',
      COMMENTARY: 'commentary',
      OPTA_COMMENTARY: 'commentary/fixture',
      LEAGUE_TABLES_GROUPS: 'league-table/groups/latest',
      LIVE_SCORES: 'live-scores',
      LIVE_LEAGUE_TABLES: 'league-table/snapshot',
    },
  } as const,
  LEAGUE_TABLES_DATA: {
    RANKING_COLOURS: {
      FIRST_RANKING_DIVIDER_COLOUR: '#DEE2E6',
      SECOND_RANKING_DIVIDER_COLOUR: '#DEE2E6',
      BOTTOM_RANKING_DIVIDER_COLOUR: '#DEE2E6',
    },
  } as const,
} as const;

export default templateConfig;
